import { Plus } from 'lucide-react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { ChatAgent, ChatTrigger } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  emptyAction,
  emptyCondition,
  emptyTrigger,
  fromTrigger,
  toCreateTrigger,
  toUpdateTrigger,
} from '../api/trigger-mappers';
import { useServerMessage } from '../hooks/use-server-message';
import { useTriggerDiscounts } from '../hooks/use-trigger-discounts';
import { useTriggerMutations } from '../hooks/use-trigger-mutations';
import {
  ACTION_TYPES,
  MAX_ACTIONS,
  MAX_CONDITIONS,
  TRIGGER_KINDS,
  triggerSchema,
  type TriggerForm,
} from '../schemas/trigger-schema';

import { FormAlert, SwitchField, TextField } from './form-controls';
import { TriggerActionRow } from './trigger-action-row';
import { TriggerConditionRow } from './trigger-condition-row';
import { TriggerPreview } from './trigger-preview';

// Paths the form shows; any other API field error goes to the dialog's alert.
const SHOWN_FIELD =
  /^(name|kind|enabled|oncePerVisitor|rescueAfterSeconds|position|conditions|actions|conditions\.\d+\.(field|op|value|values|key)|actions\.\d+\.(body|alias|asUserId|suggestedReplies|seconds|tag|productId|discountId))$/;

export interface TriggerDialogProps {
  storeId: string;
  storeName: string;
  currency: string;
  trigger: ChatTrigger | null;
  nextPosition: number;
  agents: ChatAgent[] | null;
  suggestedRepliesEnabled: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Add or change a trigger: when it applies (all conditions), what it does, and a preview. */
export function TriggerDialog({
  storeId,
  storeName,
  currency,
  trigger,
  nextPosition,
  agents,
  suggestedRepliesEnabled,
  onOpenChange,
}: TriggerDialogProps) {
  const { t } = useTranslation(['chatSettings', 'common']);
  const serverMessage = useServerMessage();
  const { create, update } = useTriggerMutations(storeId);
  const form = useForm<TriggerForm>({
    resolver: useI18nResolver(triggerSchema),
    defaultValues: trigger ? fromTrigger(trigger, currency) : emptyTrigger(nextPosition),
  });
  const conditions = useFieldArray({ control: form.control, name: 'conditions' });
  const actions = useFieldArray({ control: form.control, name: 'actions' });
  const kind = useWatch({ control: form.control, name: 'kind' });
  const watchedActions = useWatch({ control: form.control, name: 'actions' });
  const hasDiscount = watchedActions.some((action) => action.type === 'discount');
  const discounts = useTriggerDiscounts(storeId, hasDiscount);
  const saving = create.isPending || update.isPending;
  const errors = form.formState.errors;
  const listError = (list: typeof errors.conditions | typeof errors.actions) =>
    list?.root?.message ?? list?.message;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      if (trigger) {
        await update.mutateAsync({ id: trigger.id, body: toUpdateTrigger(values, currency) });
      } else {
        await create.mutateAsync(toCreateTrigger(values, currency));
      }
      onOpenChange(false);
    } catch (error) {
      const applied = applyFieldErrors(error, form.setError, {
        mapField: (field) => (SHOWN_FIELD.test(field) ? field : 'root.server'),
      });
      if (!applied) form.setError('root.server', { type: 'server', message: serverMessage(error) });
    }
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{trigger ? t('triggers.editTitle') : t('triggers.newTitle')}</DialogTitle>
          <DialogDescription>{t('triggers.dialogDescription')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-5">
            <FormAlert message={errors.root?.server?.message} />
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField control={form.control} name="name" label={t('triggers.name')} />
              <FormField
                control={form.control}
                name="kind"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('triggers.kind')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TRIGGER_KINDS.map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`triggers.kinds.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>{t(`triggers.kindHints.${kind}`)}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {kind === 'rescuer' ? (
              <TextField
                control={form.control}
                name="rescueAfterSeconds"
                label={t('triggers.rescueAfter')}
                description={t('triggers.rescueAfterHint')}
                numeric
                suffix={t('triggers.units.seconds')}
              />
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <SwitchField control={form.control} name="enabled" label={t('triggers.enabled')} />
              <SwitchField
                control={form.control}
                name="oncePerVisitor"
                label={t('triggers.oncePerVisitor')}
                description={
                  hasDiscount
                    ? t('triggers.oncePerVisitorDiscount')
                    : t('triggers.oncePerVisitorHint')
                }
                disabled={hasDiscount}
              />
            </div>

            <section className="space-y-3" aria-labelledby="trigger-conditions">
              <div>
                <h3 id="trigger-conditions" className="text-sm font-medium">
                  {t('triggers.conditions')}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {kind === 'rescuer'
                    ? t('triggers.conditionsRescuerHint')
                    : t('triggers.conditionsHint')}
                </p>
              </div>
              {conditions.fields.length > 0 ? (
                <ul className="space-y-2">
                  {conditions.fields.map((row, index) => (
                    <TriggerConditionRow
                      key={row.id}
                      form={form}
                      index={index}
                      rescuer={kind === 'rescuer'}
                      currency={currency}
                      onRemove={() => {
                        conditions.remove(index);
                      }}
                    />
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground text-sm">{t('triggers.noConditions')}</p>
              )}
              {listError(errors.conditions) ? (
                <p role="alert" className="text-destructive text-sm">
                  {listError(errors.conditions)}
                </p>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={conditions.fields.length >= MAX_CONDITIONS}
                onClick={() => {
                  conditions.append(emptyCondition(kind === 'rescuer' ? 'agentsOnline' : 'url'));
                }}
              >
                <Plus aria-hidden />
                {t('triggers.addCondition')}
              </Button>
            </section>

            <section className="space-y-3" aria-labelledby="trigger-actions">
              <div>
                <h3 id="trigger-actions" className="text-sm font-medium">
                  {t('triggers.actions')}
                </h3>
                <p className="text-muted-foreground text-sm">{t('triggers.actionsHint')}</p>
              </div>
              <ul className="space-y-2">
                {actions.fields.map((row, index) => (
                  <TriggerActionRow
                    key={row.id}
                    form={form}
                    index={index}
                    count={actions.fields.length}
                    storeId={storeId}
                    agents={agents}
                    discounts={discounts.data}
                    onMove={(to) => {
                      actions.move(index, to);
                    }}
                    onRemove={() => {
                      actions.remove(index);
                    }}
                  />
                ))}
              </ul>
              {listError(errors.actions) ? (
                <p role="alert" className="text-destructive text-sm">
                  {listError(errors.actions)}
                </p>
              ) : null}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={actions.fields.length >= MAX_ACTIONS}
                  >
                    <Plus aria-hidden />
                    {t('triggers.addAction')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {ACTION_TYPES.map((type) => (
                    <DropdownMenuItem
                      key={type}
                      disabled={type === 'discount' && hasDiscount}
                      onSelect={() => {
                        actions.append(emptyAction(type));
                      }}
                    >
                      {t(`triggers.actionTypes.${type}`)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </section>

            <section className="space-y-2" aria-labelledby="trigger-preview">
              <h3 id="trigger-preview" className="text-sm font-medium">
                {t('triggers.preview')}
              </h3>
              <TriggerPreview
                actions={watchedActions}
                storeName={storeName}
                agents={agents}
                discounts={discounts.data}
                suggestedRepliesEnabled={suggestedRepliesEnabled}
              />
            </section>

            <TextField
              control={form.control}
              name="position"
              label={t('triggers.position')}
              description={t('triggers.positionHint')}
              numeric
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? t('common:actions.saving') : t('common:actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
